// React
import { useHistory, useParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';

// Containers Injections
import { APIClient, useLoading } from '../../helpers/helpersInjection';
import { CredentialsNTInjection } from '../../containers/credentialsNTInjection';

// Yup Validation
import * as Yup from 'yup';
import { useFormik } from 'formik';

// Utils
import '../../../../utils/validation/cpfMethodValidation';

// View
import { CredentialsNTFormPage } from './view';

export const CredentialsNTForm = () => {
  const [image, setImage] = useState(null);
  const [check, setCheck] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isUpdate, setIsUpdate] = useState(false);
  const [typeMask, setTypeMask] = useState('CPF');
  const [activeTab, setActiveTab] = useState('1');
  const [credential, setCredential] = useState(null);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const api = new APIClient();
  const history = useHistory();
  const { duplicateId } = useParams();
  const { loadingWithTime } = useLoading();

  const {
    providerSpecialty,
    updateCredentialsNT,
    getProviderSpecialty,
    createProviderSpecialty,
  } = CredentialsNTInjection();

  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const getCredentialsNTById = useCallback(async (id) => {
    if (id !== 'novo') {
      const credential = await api.get(`/register/provider/${id}`);

      if (credential) {
        setCredential(credential);
        setCredential(credential);
        setIsActive(credential.active === true ? true : false);
        setCheck(credential.online_service === 't' ? true : false);
        setTypeMask(
          credential.cnpj_cpf && credential.cnpj_cpf.length === 11
            ? 'CPF'
            : 'CNPJ'
        );
        setUpdatedAt(credential.last_update);
      }
    }

    setLoading(false);
  }, []);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      // principal
      nameInput: credential ? credential.name : '',
      cpfInput: credential ? credential.cnpj_cpf : '',
      obsInput: credential ? credential.obs : '',
      logo: credential ? credential.logo : '',
      // contact
      contactInput: credential ? credential.contact : '',
      phone1Input: credential ? credential.phone1 : '',
      phone2Input: credential ? credential.phone2 : '',
      emailInput: credential ? credential.email : '',
      whatsappInput: credential ? credential.whatsapp : '',
      siteInput: credential ? credential?.social_media?.site : '',
      instagramInput: credential ? credential?.social_media?.ig : '',
      // address
      zipInput: credential ? credential.zipcode : '',
      streetInput: credential ? credential.address1 : '',
      numberInput: credential ? credential.address_num : '',
      address2Input: credential ? credential.address2 : '',
      neighborhoodInput: credential ? credential.district : '',
      cityInput: credential
        ? { value: credential.city_id, label: credential.city_name }
        : '',
      isActiveSelect: credential && (credential.active === true ? true : false),
      isOnlineService: check,
      isOnlineServiceRequired: !credential && check === false,
    },
    validationSchema: Yup.object({
      // principal
      nameInput: Yup.string().nullable().required('Nome é obrigatório'),
      cpfInput: credential
        ? Yup.string().nullable()
        : typeMask === 'CPF'
        ? Yup.string().cpfType('Digite um CPF válido').nullable()
        : Yup.string().cnpjType('Digite um CNPJ válido').nullable(),
      obsInput: Yup.string().nullable(),
      logo: Yup.string().nullable(),
      // contact
      contactInput: Yup.string().nullable(),
      phone1Input: Yup.string().nullable().required('Celular é obrigatório'),
      phone2Input: Yup.string().nullable(),
      emailInput: Yup.string().nullable(),
      whatsappInput: Yup.string().nullable(),
      siteInput: Yup.string().nullable(),
      instagramInput: Yup.string().nullable(),
      // address
      zipInput: Yup.string()
        .nullable()
        .when('isOnlineServiceRequired', {
          is: true,
          then: Yup.string().nullable().required('CEP é obrigatório'),
          otherwise: Yup.string().nullable(),
        }),
      streetInput: Yup.string()
        .nullable()
        .when('isOnlineServiceRequired', {
          is: true,
          then: Yup.string().nullable().required('Logradouro é obrigatório'),
          otherwise: Yup.string().nullable(),
        }),
      numberInput: Yup.string()
        .nullable()
        .when('isOnlineServiceRequired', {
          is: true,
          then: Yup.string().nullable().required('Número é obrigatório'),
          otherwise: Yup.string().nullable(),
        }),
      address2Input: Yup.string().nullable(),
      neighborhoodInput: Yup.string()
        .nullable()
        .when('isOnlineServiceRequired', {
          is: true,
          then: Yup.string().nullable().required('Bairro é obrigatório'),
          otherwise: Yup.string().nullable(),
        }),
      cityInput: Yup.object().when('isOnlineServiceRequired', {
        is: true,
        then: Yup.object().nullable().required('Cidade é obrigatório'),
        otherwise: Yup.object().nullable(),
      }),

      isActiveSelect: Yup.string().nullable(),
      isOnlineService: Yup.boolean().nullable(),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        id: credential ? credential.id : '',
        name: values.nameInput || null,
        cnpj_cpf: values.cpfInput || null,
        address1: values.streetInput || null,
        address_num: values.numberInput || null,
        address2: values.address2Input || null,
        district: values.neighborhoodInput || null,
        zipcode: values.zipInput || null,
        city_id: values.cityInput.value || null,
        contact: values.contactInput || null,
        email: values.emailInput || null,
        obs: values.obsInput || null,
        phone1: values.phone1Input || null,
        phone2: values.phone2Input || null,
        phone3: values.whatsappInput || null,
        social_media: {
          site: values.siteInput || null,
          ig: values.instagramInput || null,
        },
        logo: image || undefined,
        active: isActive,
        online_service: values.isOnlineService,
      };

      loadingWithTime(1000);
      if (isUpdate) {
        updateCredentialsNT({ id: credential.id, data: valuesToSubmit });
        history.push('/rede-credenciada');
      } else {
        api
          .create('/register/provider', {
            ...valuesToSubmit,
            id: '',
          })
          .then((response) => {
            if (isDuplicate) {
              providerSpecialty.map((item) => {
                return createProviderSpecialty({
                  provider_id: response.id,
                  specialty_id: item.specialty_id,
                  benefits: item.benefits,
                });
              });
            }
            history.push(`/rede-credenciada/edit/${response.id}`);
          })
          .catch((error) => {
            console.log(error.response);
          });
      }
    },
  });

  useEffect(() => {
    const { pathname } = window.location;
    const url_array = pathname.split('/');

    const last_segment = url_array[url_array.length - 1];
    getCredentialsNTById(last_segment);

    if (last_segment !== 'novo') {
      if (duplicateId) {
        getProviderSpecialty(last_segment);
        setIsDuplicate(true);
      } else {
        setIsUpdate(true);
      }
    }
  }, [duplicateId, getCredentialsNTById, getProviderSpecialty]);

  return (
    <CredentialsNTFormPage
      // states & utils
      image={image}
      check={check}
      history={history}
      loading={loading}
      isUpdate={isUpdate}
      typeMask={typeMask}
      setImage={setImage}
      isActive={isActive}
      setCheck={setCheck}
      activeTab={activeTab}
      validation={validation}
      setTypeMask={setTypeMask}
      setIsActive={setIsActive}
      // functions
      tabChange={tabChange}
      updatedAt={updatedAt}
      // data
      credential={credential || null}
      logo={credential ? credential.logo : ''}
      credentialName={credential ? credential.name : ''}
      credentialEmail={credential ? credential.email : ''}
    />
  );
};
